import {
  all,
  call,
  put,
  takeLatest,
} from 'redux-saga/effects';

import {
  setLoadingState,
  setUpReadingFeedbackByLink,
  openReadingFeedbackPopup,
  handleRequestError,
  setReadingFeedbackWebViewMode,
} from 'actions/appActions';
import {
  setAuthToken,
  setBearerToken,
} from 'actions/authActions';
import { setCurrentUser, setExtId, setSideUser } from 'actions/chatActions';
import {
  SET_UP_READING_FEEDBACK_BY_LINK,
} from 'actions/actionsTypes';
import {
  TOKEN_AUTH_STORAGE,
  TOKEN_BEARER,
} from 'constants/constants';
import { View } from 'constants/enums';
import { LocalStorage } from 'src/utils/storageHandler';
import { API } from 'src/utils/api';
import { SentryMethods } from 'src/utils/sentryMethods';
import { descriptionFromError } from 'src/utils/commonUtil';
import { formatReadingTime } from 'src/utils/dateHandler';
import { resetLastSavedPayload } from 'src/redux/sagas/readingFeedback/readingFeedbackSaga';

function* handleSetUpReadingFeedbackByLink({
  payload: {
    token,
    tokenBearer,
    psychicId,
    customerRefId,
    taskId,
    lastReading,
  },
}: ReturnType<typeof setUpReadingFeedbackByLink>) {
  try {
    resetLastSavedPayload();
    yield put(setLoadingState(true));
    yield put(setReadingFeedbackWebViewMode(true));
    yield put(setAuthToken(token));
    yield put(setBearerToken(tokenBearer));
    LocalStorage.setItem(TOKEN_AUTH_STORAGE, token);
    LocalStorage.setItem(TOKEN_BEARER, tokenBearer);

    const readingTime = lastReading ? formatReadingTime(lastReading) : '';

    const [customer, psychic] = yield all([
      call(API.Customer.getCustomerById, customerRefId, false, true),
      call(API.Psychic.getPsychic, psychicId),
    ]);

    const customerData = customer?.data || {};
    const psychicData = psychic?.data || {};
    const customerId = customerData?.customerRefId;
    const customerName = customerData?.displayName || customerData?.firstName;

    yield put(setSideUser({
      ...customerData,
      attributes: { participantType: View.CUSTOMER },
      friendlyName: customerName,
    }));

    yield put(setCurrentUser({
      ...psychicData,
      attributes: { participantType: View.PSYCHIC },
      friendlyName: psychicData.lineName,
      extId: Number(psychicId),
    }));

    yield put(setExtId(psychicId));

    yield put(openReadingFeedbackPopup({
      taskId,
      customerId,
      psychicExtId: Number(psychicId) || 0,
      customerName,
      firstName: customerData?.firstName,
      readingTime,
      isWebViewMode: true,
    }));
  } catch (e) {
    console.log(e);
    yield put(handleRequestError({
      redirectPath: '/login',
      isInvalidToken: false,
      errorText: e?.message,
      description: descriptionFromError(e),
    }));
    yield call(SentryMethods.captureException, e);
  } finally {
    yield put(setLoadingState(false));
  }
}

export function* setUpReadingFeedbackByLinkWatcher() {
  yield takeLatest(SET_UP_READING_FEEDBACK_BY_LINK, handleSetUpReadingFeedbackByLink);
}
